/**
 * Возврат в календарь на тот же день и ту же неделю (02.10.2026, сборка 1.1.1).
 *
 * Вход в разговорную комнату и на живой урок в приложении — только из календаря, а «назад» из них ведёт на
 * `/app/schedule`. Страница расписания при уходе размонтируется, её день, неделя и вид лежат в `useState` — без
 * этой памяти ученик, открывший комнату из четверга следующей недели, возвращался бы на сегодня.
 *
 * Перед «Войти» страница кладёт сюда, где стоял календарь; при следующем открытии забирает — ОДИН РАЗ.
 *
 * ⚠️ ПОЧЕМУ НЕ В АДРЕСЕ. Выход из комнаты — замена записи истории на `/app/schedule` (`pages/live/leaveCall.ts`),
 * а не шаг назад: параметры адреса прежней записи туда не попадут.
 *
 * ⚠️ ПОЧЕМУ `sessionStorage`, А НЕ ПЕРЕМЕННАЯ. Кнопка «Подключиться заново» в живом уроке перезагружает страницу
 * (`LiveRoomPage`), переменная этого не переживёт. Хранилище может быть недоступно (частный режим, сбой) — тогда
 * календарь просто откроется на сегодня, как раньше.
 *
 * ⚠️ СРОК. Запись, которую не забрали (ученик ушёл из комнаты не в календарь), через шесть часов не действует:
 * иначе завтрашнее открытие календаря с главной показало бы вчерашнюю неделю.
 */
export type ScheduleViewMode = 'month' | 'week' | 'day';

export type SchedulePlace = {
  /** Якорь недели, месяца или дня. */
  cursor: Date;
  /** Выбранный день на широком экране; `null` — не выбирали. */
  selectedDay: Date | null;
  view: ScheduleViewMode;
};

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const KEY = 'onebilim:schedule-return';
export const SCHEDULE_RETURN_TTL_MS = 6 * 3_600_000;
const VIEWS: ScheduleViewMode[] = ['month', 'week', 'day'];

function sessionStore(): Store | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function rememberSchedulePlace(place: SchedulePlace, now: Date = new Date(), store: Store | null = sessionStore()): void {
  try {
    store?.setItem(
      KEY,
      JSON.stringify({
        cursor: place.cursor.getTime(),
        selectedDay: place.selectedDay ? place.selectedDay.getTime() : null,
        view: place.view,
        at: now.getTime(),
      }),
    );
  } catch {
    // Нет хранилища — нет памяти; вход в комнату от этого не зависит.
  }
}

/** Стереть запомненное место: оно разовое. */
export function clearSchedulePlace(store: Store | null = sessionStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // Нет хранилища — нечего стирать.
  }
}

/** Забрать запомненное место. Запись стирается при любом исходе: второй раз её не будет. */
export function takeSchedulePlace(now: Date = new Date(), store: Store | null = sessionStore()): SchedulePlace | null {
  const place = peekSchedulePlace(now, store);
  clearSchedulePlace(store);
  return place;
}

/**
 * Прочитать запомненное место, не стирая. Нужен странице: начальное состояние React в режиме разработки
 * вычисляет дважды, и чтение со стиранием отдало бы второму вызову пустоту. Страница читает здесь, а стирает
 * после монтирования (`clearSchedulePlace`).
 */
export function peekSchedulePlace(now: Date = new Date(), store: Store | null = sessionStore()): SchedulePlace | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { cursor?: unknown; selectedDay?: unknown; view?: unknown; at?: unknown };
    if (typeof v.cursor !== 'number' || typeof v.at !== 'number') return null;
    const age = now.getTime() - v.at;
    if (age < 0 || age > SCHEDULE_RETURN_TTL_MS) return null;
    const cursor = new Date(v.cursor);
    if (Number.isNaN(cursor.getTime())) return null;
    const selectedDay = typeof v.selectedDay === 'number' ? new Date(v.selectedDay) : null;
    return {
      cursor,
      selectedDay: selectedDay && !Number.isNaN(selectedDay.getTime()) ? selectedDay : null,
      view: VIEWS.includes(v.view as ScheduleViewMode) ? (v.view as ScheduleViewMode) : 'week',
    };
  } catch {
    return null;
  }
}
